import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Spinner
} from "./Common/Tailwind";
import { Badge, type BadgeProps } from "./ui/badge";
import { Link } from "react-router-dom";
import { inquiryService } from "../api/services/inquiry.service";
import { useAuthContext } from "./AuthContext";
import { useUserStore } from "../store/store";
import { SUBDOMAIN } from "../assets/meta/meta";
import { stripHtmlTags } from "../utils/text";
import moment from "moment";
import "moment/locale/ko";

moment.locale('ko');

interface NewsItem {
  id: number;
  mode: string | number;
  written_date: string;
  updated_date: string;
  is_notice: boolean;
  title: string;
  content: string;
  user_full_name: string;
  images?: { image: string }[];
}

interface NewsSectionProps {
  className?: string;
  showHeader?: boolean;
  maxItems?: number;
}

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

type ModeColor = "primary" | "info" | "warning" | "secondary";

const getOutlineVariant = (color: ModeColor): BadgeVariant => `outline-${color}` as BadgeVariant;

const StyledWrapper = styled.div`
  .news-item:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
  
  .news-content {
    max-height: 400px;
    overflow-y: auto;
  }
  
  .pulse-badge {
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const NewsSection: React.FC<NewsSectionProps> = ({
  className = "",
  showHeader = true,
  maxItems = 5
}) => {
  const { isUserLogin } = useAuthContext();
  const [site] = useUserStore((state: any) => [state.site]);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('4');
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchNews();
  }, [activeTab]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use inquiry API to fetch announcements (mode 4) and FAQs (mode 5)
      const subdomain = SUBDOMAIN || site?.subdomain || '';

      if (activeTab === '4' || activeTab === '5') {
        // Use the enhanced API call for public announcements
        const response: any = await inquiryService.getPublicAnnouncements(subdomain, maxItems);
        let newsData: NewsItem[] = Array.isArray(response.data) ? response.data : [];

        // Sort by written_date descending
        newsData = newsData.sort((a, b) => new Date(b.written_date).getTime() - new Date(a.written_date).getTime());

        setNews(newsData);
      } else {
        // Fall back to regular article list for other modes
        const response: any = await inquiryService.getArticleList(subdomain, activeTab);
        let newsData: NewsItem[] = Array.isArray(response.data) ? response.data : [];

        // Sort by written_date descending and limit results
        newsData = newsData
          .sort((a, b) => new Date(b.written_date).getTime() - new Date(a.written_date).getTime())
          .slice(0, maxItems);

        setNews(newsData);
      }
    } catch (err) {
      console.error('Failed to fetch news:', err);
      setError('공지사항을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewsClick = async (newsItem: NewsItem) => {
    try {
      // Fetch full article details
      const response: any = await inquiryService.getArticleById(String(newsItem.id));
      setSelectedNews(response.data);
      setModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch news details:', err);
      setError('공지사항 상세내용을 불러오는데 실패했습니다.');
    }
  };

  const getModeInfo = (mode: string | number) => {
    const modeStr = String(mode);
    const modeConfig: Record<string, { color: ModeColor; text: string; icon: string }> = {
      '4': { color: 'primary', text: '공지사항', icon: 'mdi-bullhorn' },
      '5': { color: 'info', text: 'FAQ', icon: 'mdi-help-circle' },
      '3': { color: 'warning', text: '리뷰', icon: 'mdi-star' },
      '2': { color: 'secondary', text: '문의', icon: 'mdi-comment-question' }
    };

    return modeConfig[modeStr] || { color: 'secondary', text: '기타', icon: 'mdi-circle-small' };
  };

  const getPriorityIndicator = (newsItem: NewsItem) => {
    // Check if it's a recent announcement (within 7 days)
    const isRecent = moment().diff(moment(newsItem.written_date), 'days') <= 7;
    const isNotice = newsItem.is_notice;

    if (isNotice && isRecent) {
      return <Badge className="pulse-badge" variant="outline-success">NEW</Badge>;
    } else if (isNotice) {
      return <Badge variant="outline-warning">중요</Badge>;
    }
    return null;
  };

  if (loading) {
    return (
      <StyledWrapper className={`news-section ${className}`}>
        {showHeader && (
          <div className="flex justify-between items-center mb-3">
            <h5 style={{ fontFamily: "Gmarket-M", color: "#333", margin: 0 }}>
              📢 공지사항
            </h5>
          </div>
        )}
        <div className="text-center py-4">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">공지사항을 불러오는 중...</p>
        </div>
      </StyledWrapper>
    );
  }

  if (error) {
    return (
      <StyledWrapper className={`news-section ${className}`}>
        {showHeader && (
          <div className="flex justify-between items-center mb-3">
            <h5 style={{ fontFamily: "Gmarket-M", color: "#333", margin: 0 }}>
              📢 공지사항
            </h5>
          </div>
        )}
        <Alert color="warning" className="text-center">
          <p className="mb-2">{error}</p>
          <Button variant="primary" size="sm" onClick={fetchNews}>
            다시 시도
          </Button>
        </Alert>
      </StyledWrapper>
    );
  }

  return (
    <StyledWrapper className={`news-section ${className}`}>
      {showHeader && (
        <div className="flex justify-between items-center mb-3">
          <h5 style={{ fontFamily: "Gmarket-M", color: "#333", margin: 0 }}>
            소식
          </h5>
          <Link to="/inquiry" className="no-underline">
            <small style={{ color: "#666" }}>전체보기 ›</small>
          </Link>
        </div>
      )}

      {/* Category Tabs */}
      <Nav tabs className="mb-3" style={{ fontSize: '14px' }}>
        <NavItem>
          <NavLink
            active={activeTab === '4'}
            onClick={() => setActiveTab('4')}
            style={{ cursor: 'pointer', padding: '8px 12px' }}
          >
            공지사항
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            active={activeTab === '5'}
            onClick={() => setActiveTab('5')}
            style={{ cursor: 'pointer', padding: '8px 12px' }}
          >
            FAQ
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            active={activeTab === '3'}
            onClick={() => setActiveTab('3')}
            style={{ cursor: 'pointer', padding: '8px 12px' }}
          >
            리뷰
          </NavLink>
        </NavItem>
      </Nav>

      {/* News List */}
      {news.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted">표시할 내용이 없습니다.</p>
        </div>
      ) : (
        <div className="news-list">
          {news.map((newsItem) => {
            const modeInfo = getModeInfo(newsItem.mode);
            const priorityBadge = getPriorityIndicator(newsItem);
            const previewText = stripHtmlTags(newsItem.content || "");

            return (
              <Card
                key={newsItem.id}
                className="mb-2 news-item"
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: '1px solid #eee'
                }}
                onClick={() => handleNewsClick(newsItem)}
              >
                <CardBody className="py-2 px-3">
                  <Row className="items-center">
                    <Col xs={1} className="flex justify-center">
                      <i className={`mdi ${modeInfo.icon} text-${modeInfo.color}`}></i>
                    </Col>
                    <Col xs={8}>
                      <div className="flex items-center mb-1">
                        <Badge variant={getOutlineVariant(modeInfo.color)}>{modeInfo.text}</Badge>
                        {priorityBadge && <span className="ms-1">{priorityBadge}</span>}
                        <small className="text-muted ms-2">
                          {moment(newsItem.written_date).fromNow()}
                        </small>
                      </div>
                      <h6
                        className="mb-0 text-truncate"
                        style={{
                          fontFamily: "Gmarket-M",
                          fontSize: '14px',
                          color: '#333'
                        }}
                      >
                        {newsItem.title}
                      </h6>
                      {previewText && (
                        <p className="mb-0 text-muted text-truncate" style={{ fontSize: '12px' }}>
                          {previewText}
                        </p>
                      )}
                    </Col>
                    <Col xs={3} className="text-end">
                      <i className="mdi mdi-chevron-right text-muted"></i>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      {/* News Detail Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} variant="full">
        <div className="flex h-full flex-col bg-white">
          <ModalHeader toggle={() => setModalOpen(false)} showBack>
            {selectedNews && (
              <div className="flex items-center">
                <Badge variant={getOutlineVariant(getModeInfo(selectedNews.mode).color)}>
                  {getModeInfo(selectedNews.mode).text}
                </Badge>
                <span className="ms-2">{selectedNews.title}</span>
              </div>
            )}
          </ModalHeader>
          <ModalBody className="flex-1 overflow-y-auto p-6">
            {selectedNews && (
              <div>
                <div className="mb-3">
                  <small className="text-muted">
                    작성일: {moment(selectedNews.written_date).format('YYYY년 MM월 DD일 HH:mm')}
                    {selectedNews.updated_date !== selectedNews.written_date && (
                      <span className="ms-2">
                        (수정일: {moment(selectedNews.updated_date).format('YYYY년 MM월 DD일 HH:mm')})
                      </span>
                    )}
                  </small>
                  <br />
                  <small className="text-muted">
                    작성자: {selectedNews.user_full_name || '관리자'}
                  </small>
                </div>
                <div
                  className="news-content"
                  style={{
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedNews.content || '' }}
                >
                </div>

                {/* Show images if any */}
                {selectedNews.images && selectedNews.images.length > 0 && (
                  <div className="mt-3">
                    <h6>첨부 이미지</h6>
                    <div className="flex flex-wrap gap-2">
                      {selectedNews.images.map((image, idx) => (
                        <img
                          key={idx}
                          src={image.image}
                          alt={`첨부 이미지 ${idx + 1}`}
                          className="img-fluid rounded"
                          style={{ maxWidth: '200px', maxHeight: '200px' }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter className="bg-white">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              닫기
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </StyledWrapper>
  );
};

export default NewsSection;
